import React, { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import { Input } from "components/input/Input";
import { BiCalendar } from "react-icons/bi";
import { useForm } from "react-hook-form";
import { ReactComponent as InfoFill } from "assets/icons/info-fill.svg";
import { isDatePastToday, isInThePast } from "utils/helpers";
import { Button } from "components/buttons";
import { MdWarning } from "react-icons/md";
import { GrCheckmark } from "react-icons/gr";
import { BiRightArrow } from "react-icons/bi";
import RadioSelectorDrop from "./RadioSelectorDrop";
import { HiArrowNarrowRight } from "react-icons/hi";
import PaymentMethod from "./PaymentMethod";
import VaultHolder from "./VaultHolder";
import { successToast, warningToast } from "components/toast/toast";
import { useQuery } from "@apollo/client";
import { COUPONS } from "GraphQL/Queries";

import {
  checkDeadlineRequirement,
  subtractDaysAndKeepISOFormat,
} from "utils/helpers";

const SelectionDetails = ({ _package, close }) => {
  const {
    control,
    handleSubmit,
    watch,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: {
      packageTier: "", // Default selected tier
      accomodation: "", // Provide a default accomodation value
    },
  });

  const [rate, setRate] = useState(0);
  const [pckg, setPckg] = useState(1);
  const [selectedWeek, setSelectedWeeks] = useState(1);
  const [sortedRates, setSortedRates] = useState([]);
  const [paymentType, setPaymentType] = useState("");
  const [dateValidationStatus, setDateValidationStatus] = useState({
    error: "",
    warning: "",
  });

  const [warning, setWarning] = useState(false);

  const [vaultModal, setVaultModal] = useState(null);
  const [selectedCoupon, setSelectedCoupon] = useState({
    couponCode: "",
    percentage: null,
  });

  const { data } = useQuery(COUPONS);

  const availableCoupons = useMemo(() => {
    return data?.me.user.coupons?.filter(
      (item) =>
        (!item?.usedCoupon && isDatePastToday(item.couponId.expiresAt)) || [],
    );
  }, [data?.me.user.coupons]);

  const occupancyType = watch("accomodation");
  const tierType = watch("packageTier");
  const departureDate = watch("departure_date");

  const occupancy = [
    {
      id: "SINGLE",
      title: "Single Occupancy",
      description:
        "Single occupancy flights are for travelers who want to stay alone in their hotel room, please note this will require more cost.",
    },
    {
      id: "DOUBLE",
      title: "Double Occupancy",
      description:
        "Double occupancy flights are for travelers who are willing to share their hotel room.",
    },
  ];

  const handleNumbers = useCallback(
    (type) => {
      if (type === "increment" && pckg < 12) {
        setPckg((prev) => prev + 1);
      } else if (type === "decrement" && pckg > 1) {
        setPckg((prev) => prev - 1);
      }
    },
    [pckg],
  );

  // Memoize sortedRates calculation
  useEffect(() => {
    if (_package?.room_rates && _package?.room_rates.length > 0) {
      const newArray = [..._package.room_rates].sort((a, b) => {
        if (a.variant_week !== b.variant_week) {
          return a.variant_week - b.variant_week;
        } else {
          let x = a.room_type === "SINGLE" ? 1 : 2;
          let y = b.room_type === "SINGLE" ? 1 : 2;
          return y - x;
        }
      });

      setSortedRates(newArray);
    }
  }, [_package?.room_rates]);

  // Calculate total price function as a memoized callback
  const calculateTotalPrice = useCallback(
    (weeks, packageClass, roomType, pckgNo, couponPercent) => {
      if (
        !sortedRates ||
        sortedRates.length === 0 ||
        !weeks ||
        !packageClass ||
        !roomType
      )
        return 0;

      // Initialize total
      let singlePackageTotal = 0;

      // First check if there's a rate with null/undefined variant_week that matches package class and room type
      // This would represent a flat rate regardless of weeks
      const flatRate = sortedRates.find(
        (rate) =>
          (rate.variant_week === null || rate.variant_week === undefined) &&
          rate.package_class === packageClass &&
          rate.room_type === roomType,
      );

      if (flatRate) {
        // Use flat rate if available
        singlePackageTotal = parseInt(flatRate.rate);
      } else {
        // Otherwise, sum up rates for each week
        for (let weekNum = 1; weekNum <= weeks; weekNum++) {
          const matchingRate = sortedRates.find(
            (rate) =>
              rate.variant_week === weekNum &&
              rate.package_class === packageClass &&
              rate.room_type === roomType,
          );

          if (matchingRate) {
            singlePackageTotal += parseInt(matchingRate.rate);
          }
        }
      }

      // Multiply by number of packages
      return singlePackageTotal * pckgNo * ((100 - couponPercent) / 100);
    },
    [sortedRates],
  );

  // Update rate when selections change
  useEffect(() => {
    const newRate = calculateTotalPrice(
      selectedWeek,
      tierType,
      occupancyType,
      pckg,
      selectedCoupon?.percentage,
    );

    setRate(newRate);
  }, [
    selectedWeek,
    tierType,
    occupancyType,
    pckg,
    calculateTotalPrice,
    selectedCoupon?.percentage,
  ]);

  // Memoize package_class base structure
  const packageClassBase = useMemo(
    () => [
      {
        id: "BUDGET",
        title: "Budget",
        icon: "😉",
        description: "",
      },
      {
        id: "MID_RANGE",
        title: "Mid-range",
        icon: "✨",
        description: "",
      },
      {
        id: "LUXURY",
        title: "Luxury",
        icon: "💎",
        description:
          "Our luxury tier package includes high-end 5-star resorts, hotels or apartments, individual transportation in luxury SUVs/Limos, meals in 5-star restaurants, etc)",
      },
    ],
    [],
  );

  // Calculate prices for all package tiers with current selection parameters
  const packageClassWithPrices = useMemo(() => {
    return packageClassBase.map((packageType) => {
      const packagePrice = calculateTotalPrice(
        selectedWeek,
        packageType.id,
        occupancyType,
        pckg,
        "",
      );

      return {
        ...packageType,
        price: packagePrice,
      };
    });
  }, [
    packageClassBase,
    selectedWeek,
    occupancyType,
    pckg,
    calculateTotalPrice,
  ]);

  const findSelectedRoomRateId = useCallback(() => {
    if (!_package?.room_rates || !tierType || !occupancyType || !selectedWeek) {
      return null;
    }

    const selectedRate = _package.room_rates.find((rate) => {
      const matchesRoomType = rate.room_type === occupancyType;
      const matchesPackageClass = rate.package_class === tierType;
      const matchesWeek = rate.variant_week
        ? rate.variant_week === selectedWeek
        : rate.variant_week === null;

      return matchesRoomType && matchesPackageClass && matchesWeek;
    });

    return selectedRate?.id || null;
  }, [_package?.room_rates, tierType, occupancyType, selectedWeek]);

  // Format currency helper
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  }, []);

  const standardWindow = checkDeadlineRequirement(
    departureDate,
    _package?.deadline_days_from_departure,
  );

  const minExpiryWindow = checkDeadlineRequirement(
    departureDate,
    _package?.min_days_before_expiry,
  );

  const longTerm_deadline_date = useMemo(() => {
    return standardWindow
      ? subtractDaysAndKeepISOFormat(
          departureDate,
          _package.deadline_days_from_departure,
        )
      : subtractDaysAndKeepISOFormat(
          departureDate,
          _package.min_days_before_expiry,
        );
  }, [
    _package.deadline_days_from_departure,
    _package.min_days_before_expiry,
    departureDate,
    standardWindow,
  ]);

  const handleDateClick = (e) => {
    e.target.type = "date";
    e.target.min = moment(_package?.departure_date).format("YYYY-MM-DD");
    e.target.max = moment(_package?.return_date).format("YYYY-MM-DD");
  };

  // Validate date selection
  useEffect(() => {
    // Reset validation status
    setDateValidationStatus({
      error: "",
      warning: "",
    });

    if (!departureDate) {
      return;
    }

    if (_package?.duration_type === "LONG_TERM") {
      // Long term package validation

      // if past standwindow and still within minExpiry
      if (standardWindow) {
        setWarning(false);
      } else if (!standardWindow && minExpiryWindow) {
        setWarning(true);
        setDateValidationStatus({
          error: "",
          warning:
            "You are booking after the standard window period. You need to make full deposit to secure this booking.",
        });
      } else {
        setDateValidationStatus({
          error:
            "You can't book this package for this date as its booking window is closed. Select another departure date.",
          warning: "",
        });
      }
    }
  }, [departureDate, _package, standardWindow, minExpiryWindow]);

  // Check if booking is disabled
  const isBookingDisabled = useMemo(() => {
    if (dateValidationStatus.error) return true;

    if (rate < 1) return true;
    if (
      isInThePast(_package?.booking_deadline_date) &&
      _package?.duration_type !== "LONG_TERM"
    )
      return true;
    return false;
  }, [
    rate,
    dateValidationStatus.error,
    _package?.booking_deadline_date,
    _package?.duration_type,
  ]);

  const [view, setView] = useState(1); // State to manage the current view (1 or 2)

  const changeToPaymentMethod = handleSubmit(async (data) => {
    setPaymentType("FULL");
    setView(2);
  });

  const handleOpenVaultDialog = handleSubmit((data) => {
    if (!data.accomodation || !data.packageTier) {
      warningToast("Complete all available fields");
      return;
    }
    setVaultModal("dialog");
  });

  return (
    <div className="h-full bg-white-100">
      {view === 1 && (
        <div className="h-full overflow-y-hidden">
          <section>
            <div className="p-4 border-b border-black-100/10">
              <h3 className="text-[#1D2939] font-bold text-lg">
                Checkout Package
              </h3>
              <p className="text-sm text-[#525866] mt-1">
                Select package preference and proceed to pay.
              </p>
            </div>
            <ul className="p-4 flex items-center gap-x-2 ">
              <li className="flex items-center gap-x-1">
                <GrCheckmark className="bg-[#1FC16B] h-5 w-5 p-1.5 rounded-full text-white-100" />
                <span className="text-gray-500 text-xs">Package selection</span>
                <BiRightArrow className="text-[8px]" />
              </li>
              <li className="flex items-center gap-x-1">
                <span className="h-5 w-5 rounded-full bg-blue-100 text-white-100 flex items-center justify-center">
                  2
                </span>
                <span className="text-gray-500 text-xs">Checkout package</span>
                <BiRightArrow className="text-[8px]" />
              </li>
              <li className="flex items-center gap-x-1">
                <span className="h-5 w-5 rounded-full border border-black-100/30 bg-white-100 text-gray-600 flex items-center justify-center">
                  3
                </span>{" "}
                <span className="text-gray-500 text-xs">Payment method</span>
              </li>
            </ul>
          </section>

          <section
            style={{
              height: "calc(100% - 120px)",
            }}
          >
            <form className="h-full overflow-y-scroll relative ">
              <section className="px-2 lg:px-4 rounded-lg">
                <div
                  style={{
                    height: "calc(100% - 170px)",
                  }}
                  className="space-y-3 overflow-y-scroll px-2 rounded-lg bg-[#F5F7F9] w-fit py-2"
                >
                  <article className="bg-white-100 p-2">
                    <label className="text-sm font-medium text-black-100">
                      Select number of packages *
                    </label>
                    <div className="mt-2 flex items-center justify-between">
                      <button
                        type="button"
                        className="bg-[#E7E8E9] py-2 px-4 rounded-lg flex items-center justify-center text-black-100"
                        onClick={() => handleNumbers("decrement")}
                        disabled={pckg <= 1}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        value={pckg}
                        readOnly
                        className="p-0 text-center border-none outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => handleNumbers("increment")}
                        className="bg-blue-100 py-2 px-4 rounded-lg flex items-center justify-center text-white-100"
                      >
                        +
                      </button>
                    </div>
                  </article>

                  {_package?.duration_type === "LONG_TERM" && (
                    <article className="bg-white-100 p-2">
                      <label className="text-sm font-medium text-black-100">
                        Select a departure date *
                      </label>
                      <p className="text-sm text-[#667085]">
                        You can select a departure date between{" "}
                        <span>
                          {moment(_package?.departure_date).format("dddd, ll.")}
                        </span>{" "}
                        and{" "}
                        <span>
                          {moment(_package?.return_date).format("dddd, ll.")}
                        </span>
                      </p>
                      <div className="mt-2 w-full">
                        <Input
                          name="departure_date"
                          placeholder="Select a date"
                          Icon={<BiCalendar />}
                          withIcon
                          type="text"
                          onFocus={handleDateClick}
                          className="border border-[#E1E4EA] bg-white-100 rounded-xl w-full px-3 py-2"
                          inputClass="w-full"
                          register={register}
                          registerOptions={{ required: true }}
                        />
                      </div>

                      {/* Display validation errors/warnings */}

                      {dateValidationStatus.warning && (
                        <div className="mt-2 flex items-start gap-2 p-2 bg-yellow-50 rounded-md">
                          <MdWarning className="text-yellow-500 flex-shrink-0 mt-1" />
                          <p className="text-yellow-500 text-xs">
                            {dateValidationStatus.warning}
                          </p>
                        </div>
                      )}

                      {dateValidationStatus.error && (
                        <div className="mt-2 flex items-start gap-2 p-2 bg-red-50 rounded-md">
                          <MdWarning className="text-red-500 flex-shrink-0 mt-1" />
                          <p className="text-red-500 text-xs">
                            {dateValidationStatus.error}
                          </p>
                        </div>
                      )}
                    </article>
                  )}

                  <article className="bg-white-100 p-2">
                    <label className="text-sm font-medium text-black-100">
                      Select travel duration{" "}
                    </label>
                    <p className="text-xs">
                      How long will you like your trip to last?
                    </p>
                    <div>
                      <div className="flex items-center gap-x-3 mt-2">
                        {Array.from(
                          { length: _package?.variable_weeks || 0 },
                          (_, i) => (
                            <button
                              type="button"
                              className={`${
                                selectedWeek === i + 1
                                  ? "border-blue-100"
                                  : "border-black-100/10"
                              } px-4 py-2 font-medium flex items-center justify-center border rounded-lg text-sm bg-white-100`}
                              key={i}
                              onClick={() => setSelectedWeeks(i + 1)}
                            >
                              {i + 1} {i + 1 === 1 ? "Week" : "Weeks"}
                            </button>
                          ),
                        )}
                      </div>
                    </div>
                  </article>

                  <article className="bg-white-100 p-2">
                    <label className="text-sm font-medium text-black-100">
                      Select accommodation type *
                    </label>
                    <p className="text-xs">
                      What type of accommodation do you prefer?
                    </p>
                    <div className="mt-2">
                      <RadioSelectorDrop
                        levels={occupancy}
                        name="accomodation"
                        control={control}
                      />

                      {errors.accomodation && <p>Select accomodation type</p>}
                    </div>
                  </article>

                  <article className="bg-white-100 p-2">
                    <label className="text-sm font-medium text-black-100">
                      Select package tier{" "}
                    </label>
                    <p className="text-xs">
                      What type of package tier do you prefer?
                    </p>
                    <div className="mt-2">
                      <RadioSelectorDrop
                        levels={packageClassWithPrices}
                        name="packageTier"
                        control={control}
                      />

                      {errors.packageTier && <p>Select accomodation type</p>}
                    </div>
                  </article>

                  {_package?.duration_type === "LONG_TERM" && !departureDate ? (
                    ""
                  ) : (
                    <div className="bg-[#E9F6FC] flex items-start gap-2 text-sm text-[#101C28] w-full p-2 sm:p-4">
                      <InfoFill />
                      <p className="flex-1">
                        Booking for this trip ends on{" "}
                        <span className="font-medium">
                          {_package?.duration_type === "LONG_TERM"
                            ? departureDate &&
                              moment(longTerm_deadline_date).format("dddd, ll.")
                            : _package?.booking_deadline_date &&
                              moment(_package?.booking_deadline_date).format(
                                "dddd, ll.",
                              )}
                        </span>{" "}
                        . Also note that there's a non-refundable service charge
                        of $ {_package?.non_refundable_amount} after booking
                        this package.
                      </p>
                    </div>
                  )}

                  <div className="border-t border-black-100/20 py-3 text-base text-[#101C28]">
                    <span className="font-medium text-sm">SUMMARY</span>

                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-[#667085] text-base">
                        {_package?.name}
                      </span>
                      <span className="text-blue-200 font-bold text-xl">
                        X{pckg}
                      </span>
                    </div>

                    {availableCoupons && availableCoupons.length > 0 && (
                      <div className="flex flex-col gap-y-1 border-y border-black-100/10 py-2">
                        <p className="text-[#093549]/90 text-base font-medium capitalize">
                          Available Coupons
                        </p>

                        {availableCoupons?.map((item) => (
                          <div className="flex justify-between mb-2">
                            <p className="font-medium text-[#5D6D8F]/70 text-sm">
                              {item?.couponId.promotionName}{" "}
                              <span className="text-[#093549]/90">
                                ({item?.couponId.discountPercentage}% OFF)
                              </span>
                            </p>
                            <div
                              onClick={() => {
                                selectedCoupon?.couponCode ===
                                item.couponId.couponCode
                                  ? setSelectedCoupon({
                                      couponCode: "",
                                      percentage: null,
                                    })
                                  : setSelectedCoupon({
                                      couponCode: item.couponId.couponCode,
                                      percentage:
                                        item?.couponId.discountPercentage,
                                    });
                              }}
                              className="cursor-pointer w-4 h-4 aspect-square border border-blue-100 rounded-full flex items-center justify-center p-1"
                            >
                              {selectedCoupon?.couponCode ===
                                item.couponId.couponCode && (
                                <div className="w-4 h-4 aspect-square border border-blue-100 bg-blue-100 rounded-full">
                                  {" "}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-[#667085] text-base">
                        Total Price
                      </span>
                      <span className="text-blue-200 font-bold text-xl">
                        {formatCurrency(rate)}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <section className="h-[180px] w-full bg-blue-100/10 px-4 pt-4 pb-6">
                <div className="my-3">
                  <span className="text-black-90 text-base">Total Price</span>
                  <span className="block text-blue-200 font-bold text-xl">
                    {formatCurrency(rate)}
                  </span>
                </div>

                {warning ? (
                  <div className="flex sm:flex-row flex-col items-start font-bold lg:flex-row gap-2">
                    <Button
                      disabled={isBookingDisabled}
                      onClick={changeToPaymentMethod}
                      isBlue
                      text={" Book now and get 3% OFF full payment"}
                      className="text-sm !p-0 flex items-center justify-center flex-row-reverse gap-1"
                    />
                  </div>
                ) : (
                  <div className="flex sm:flex-row flex-col items-start font-bold lg:flex-row gap-2">
                    <Button
                      disabled={isBookingDisabled}
                      onClick={handleOpenVaultDialog}
                      withIcon
                      icon={<HiArrowNarrowRight size={18} fill="#1C9FDA" />}
                      text="Save for this trip"
                      className="bg-transparent !p-0 text-sm flex items-center justify-center text-blue-100 flex-row-reverse gap-1 border border-blue-100"
                    />

                    <Button
                      disabled={isBookingDisabled}
                      onClick={changeToPaymentMethod}
                      isBlue
                      text={"Book now and get 3% Off!"}
                      className="text-sm !p-0 flex items-center justify-center flex-row-reverse gap-1"
                    />
                  </div>
                )}

                {/* {isBookingDisabled ? (
                  <div className="flex flex-wrap gap-1 text-xl text-gray-100/50 font-medium">
                    {isInThePast(_package?.booking_deadline_date) &&
                    _package?.duration_type !== "LONG_TERM"
                      ? "This trip has expired..."
                      : "This package is not available for the selected date"}
                  </div>
                ) : warning ? (
                  <div className="flex sm:flex-row flex-col items-start font-bold lg:flex-row gap-2">
                    <Button
                      onClick={changeToPaymentMethod}
                      isBlue
                      text={" Book now and get 3% OFF full payment"}
                      className="text-sm !p-0 flex items-center justify-center flex-row-reverse gap-1"
                    />
                  </div>
                ) : (
                  <div className="flex sm:flex-row flex-col items-start font-bold lg:flex-row gap-2">
                    <Button
                      onClick={handleOpenVaultDialog}
                      withIcon
                      icon={<HiArrowNarrowRight size={18} fill="#1C9FDA" />}
                      text="Save for this trip"
                      className="bg-transparent !p-0 text-sm flex items-center justify-center text-blue-100 flex-row-reverse gap-1 border border-blue-100"
                    />

                    <Button
                      onClick={changeToPaymentMethod}
                      isBlue
                      text={"Book now and get 3% Off!"}
                      className="text-sm !p-0 flex items-center justify-center flex-row-reverse gap-1"
                    />
                  </div>
                )} */}
              </section>
            </form>
          </section>
        </div>
      )}

      {vaultModal === "dialog" || vaultModal === "createVault" ? (
        <VaultHolder
          packageDetails={_package}
          rate={rate}
          numberOfPackages={pckg}
          vaultModal={vaultModal}
          setVaultModal={setVaultModal}
          setView={setView}
          closeModal={close}
          departureDate={departureDate}
          longTerm_deadline_date={longTerm_deadline_date}
          setPaymentType={setPaymentType}
          selectedCoupon={selectedCoupon}
          selectedRoomRateId={findSelectedRoomRateId()}
        />
      ) : (
        ""
      )}

      {view === 2 && (
        <PaymentMethod
          selectedCoupon={selectedCoupon}
          packg={_package}
          rate={rate}
          numberOfPackages={pckg}
          close={close}
          selectedRoomRateId={findSelectedRoomRateId()}
          paymentType={paymentType}
          longTerm_deadline_date={longTerm_deadline_date}
          departureDate={departureDate}
        />
      )}
    </div>
  );
};

export default SelectionDetails;
